import { ChatProps } from "./types";
import { styles } from "./styles";
import ChatMessage from "./Chat.Message";
import ChatMessages from "./Chat.Messages";
import ChatCue from "./Chat.Cue";
import ChatCues from "./Chat.Cues";
import ChatInput from "./Chat.Input";
import { withDisplayName } from "../types";
import { useIntersectionObserver, useResizeObserver } from "../../hooks";
import { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import ChatHeader from "./Chat.Header";

/**
 * `Chat` is the main component that serves as a container for the chat interface.
 * It provides the structure and styling for displaying chat messages and message cues (suggested messages).
 *
 * Example usage:
 * ```tsx
 * <Chat>
 *   <Chat.Messages messages={messages} .... />
 *   <Chat.Input ... />
 * </Chat>
 * ```
 */
function Chat({
  children,
  noPadding,
  theme = "default",
  autoFillHeight,
  headerRef,
  footerRef
}: ChatProps) {
  const [topOffset, setTopOffset] = useState<string | undefined>(undefined);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const [bottomPadding, setBottomPadding] = useState<number>(0);
  const [topPadding, setTopPadding] = useState<number>(0);

  function adjustTopOffset() {
    if (!chatRef.current) {
      return;
    }
    const messagesContainer =
      chatRef.current.querySelector(".kit-ChatMessages");
    if (!messagesContainer) {
      return;
    }

    const top = chatRef.current.getBoundingClientRect().top || 0;
    const parentChildren = messagesContainer.parentElement?.children;
    let offset = 0;
    Array.from(parentChildren || []).forEach(child => {
      if (child !== messagesContainer) {
        offset += child.getBoundingClientRect().height || 0;
      }
    });
    setTopOffset(`${(top < 0 ? 0 : top) + offset}px`);
  }

  const handleWindowScroll = useRef<(this: Window, ev: Event) => void>(() => {
    adjustTopOffset();
  });

  const setTopOffsetRef = useIntersectionObserver({
    threshold: 0,
    rootMargin: "50px 0px 50px 0px",
    onIntersection: ([entry]) => {
      window.removeEventListener("scroll", handleWindowScroll.current);
      if (entry.isIntersecting) {
        window.addEventListener("scroll", handleWindowScroll.current, {
          passive: true
        });
      }
      adjustTopOffset();
    }
  });

  const maxHeight = !autoFillHeight
    ? "100vh"
    : `calc(100vh - ${chatRef.current?.getBoundingClientRect().top || 0}px)`;

  useEffect(() => {
    return () => {
      window.removeEventListener("scroll", handleWindowScroll.current);
    };
  }, []);

  const nullHeaderRef = useRef(null);
  const nullFooterRef = useRef(null);
  const headerRefToUse = headerRef || nullHeaderRef;
  const footerRefToUse = footerRef || nullFooterRef;

  useResizeObserver(headerRefToUse, () => {
    setTopPadding(headerRefToUse.current?.getBoundingClientRect().height || 0);
    adjustTopOffset();
  });
  useResizeObserver(footerRefToUse, () => {
    setBottomPadding(
      footerRefToUse.current?.getBoundingClientRect().height || 0
    );
    adjustTopOffset();
  });

  return (
    <>
      {autoFillHeight && <div ref={setTopOffsetRef} />}
      <div
        className={`kit-Chat ${theme}-theme`}
        css={[
          styles.chat.base,
          !noPadding && styles.chat.padded,
          css`
            --chat-height-offset: ${topOffset};
            max-height: ${maxHeight};
            --chat-messages-padding-top: ${topPadding}px;
            --chat-messages-padding-bottom: ${bottomPadding}px;
          `,
          theme === "maverick" && styles.chat.maverickTheme
        ]}
        ref={chatRef}
      >
        {children}
      </div>
    </>
  );
}

Chat.Message = withDisplayName(ChatMessage, "Chat.Message");
Chat.Messages = withDisplayName(ChatMessages, "Chat.Messages");
Chat.Input = withDisplayName(ChatInput, "Chat.Input");
Chat.Cue = withDisplayName(ChatCue, "Chat.Cue");
Chat.Cues = withDisplayName(ChatCues, "Chat.Cues");
Chat.Header = withDisplayName(ChatHeader, "Chat.Header");

export default Chat;
